/**
 * Error mapping utility for user-friendly error messages
 */

interface FriendlyError {
  message: string;
  hint?: string;
}

const errorMessages: Record<string, FriendlyError> = {
  // Auth errors
  InvalidAccountId: {
    message: "Account not found",
    hint: "Check your email or sign up for a new account",
  },
  InvalidPassword: {
    message: "Incorrect password",
    hint: "Try again or reset your password",
  },
  InvalidSecret: {
    message: "Incorrect password",
    hint: "Try again or reset your password",
  },
  EmailAlreadyExists: {
    message: "Email already registered",
    hint: "Sign in instead or use a different email",
  },
  AccountAlreadyExists: {
    message: "Account already exists",
    hint: "Sign in instead",
  },
  InvalidEmail: {
    message: "Invalid email address",
    hint: "Please enter a valid email",
  },
  TooManyRequests: {
    message: "Too many attempts",
    hint: "Please wait a moment and try again",
  },

  // Network errors
  NetworkError: {
    message: "Connection failed",
    hint: "Check your internet and try again",
  },
  Offline: {
    message: "You're offline",
    hint: "Check your connection and try again",
  },
  Timeout: {
    message: "Request timed out",
    hint: "Try again in a moment",
  },

  // Data errors
  NotFound: {
    message: "Not found",
    hint: "This item may have been deleted",
  },
  Unauthorized: {
    message: "Not authorized",
    hint: "Please sign in again",
  },

  // Generic
  default: {
    message: "Something went wrong",
    hint: "Please try again",
  },
};

/**
 * Extract error code from various error formats
 */
function extractErrorCode(error: unknown): string | null {
  if (error instanceof Error) {
    const message = error.message;

    // Check for exact matches first
    for (const code of Object.keys(errorMessages)) {
      if (message.includes(code)) {
        return code;
      }
    }

    // Check for common patterns
    if (message.toLowerCase().includes("network") || message.toLowerCase().includes("fetch")) {
      return "NetworkError";
    }
    if (message.toLowerCase().includes("timeout")) {
      return "Timeout";
    }
    if (message.toLowerCase().includes("unauthorized") || message.toLowerCase().includes("401")) {
      return "Unauthorized";
    }
    if (message.toLowerCase().includes("not found") || message.toLowerCase().includes("404")) {
      return "NotFound";
    }
  }

  return null;
}

/**
 * Convert any error to a user-friendly message with optional hint
 */
export function getUserFriendlyError(error: unknown): FriendlyError {
  const code = extractErrorCode(error);

  if (code && errorMessages[code]) {
    return errorMessages[code];
  }

  return errorMessages.default;
}

/**
 * Get just the message string (for simpler use cases)
 */
export function getErrorMessage(error: unknown): string {
  return getUserFriendlyError(error).message;
}
